// ═══════════════════════════════════════════════════════════════════════
// errorBoundary.jsx — Per-tab error isolation
// ═══════════════════════════════════════════════════════════════════════
// Wrap each tab so a render error in one tab doesn't crash the whole app.
// React 18+ class component (still the only way to do error boundaries).
// ═══════════════════════════════════════════════════════════════════════

import React from "react";
import { T, FONTS } from "./theme";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, attempt: 0 };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Best-effort console reporting; real apps would send to telemetry.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary · tab=${this.props.tabName || "unknown"}]`,
      error,
      info?.componentStack,
    );
  }

  reset = () => {
    this.setState((prev) => ({
      error: null,
      info: null,
      attempt: prev.attempt + 1,
    }));
  };

  copyToClipboard = () => {
    const { error, info } = this.state;
    const text =
      `Tab: ${this.props.tabName || "unknown"}\n` +
      `Error: ${error?.message}\n\n` +
      `Stack:\n${error?.stack || "(no stack)"}\n\n` +
      `Component stack:${info?.componentStack || "(no component stack)"}`;
    try {
      navigator.clipboard?.writeText(text);
    } catch {
      /* ignore */
    }
  };

  render() {
    if (!this.state.error) {
      // Re-render children when reset is clicked by keying off attempt
      return (
        <React.Fragment key={this.state.attempt}>
          {this.props.children}
        </React.Fragment>
      );
    }

    const { error, info } = this.state;
    const tabName = this.props.tabName || "this tab";

    return (
      <div
        style={{
          padding: "40px 24px",
          maxWidth: 900,
          margin: "40px auto",
          background: T.bg2,
          border: `1px solid ${T.red}40`,
          borderLeft: `4px solid ${T.red}`,
          borderRadius: 8,
          fontFamily: FONTS.body,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
          <span style={{ fontSize: 38 }}>⚠️</span>
          <div>
            <div
              style={{
                fontFamily: FONTS.header,
                fontSize: 18,
                fontWeight: 700,
                color: T.red,
                letterSpacing: 1,
              }}
            >
              SOMETHING WENT WRONG IN <span style={{ textTransform: "uppercase" }}>{tabName}</span>
            </div>
            <div style={{ fontSize: 11, color: T.text2, marginTop: 4 }}>
              The rest of the app is still working. Switch tabs or try to recover.
            </div>
          </div>
        </div>

        <div
          style={{
            background: T.bg3,
            border: `1px solid ${T.red}25`,
            borderRadius: 6,
            padding: "12px 14px",
            marginBottom: 14,
          }}
        >
          <div
            style={{
              fontSize: 8,
              color: T.red,
              letterSpacing: 1,
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Error message
          </div>
          <div
            style={{
              fontFamily: FONTS.code,
              fontSize: 11,
              color: T.text,
              wordBreak: "break-word",
            }}
          >
            {error?.message || String(error)}
          </div>
        </div>

        {error?.stack && (
          <details
            style={{
              background: T.bg3,
              border: `1px solid ${T.border}`,
              borderRadius: 6,
              padding: "10px 14px",
              marginBottom: 14,
            }}
          >
            <summary
              style={{
                fontSize: 9,
                color: T.amber,
                cursor: "pointer",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Stack trace
            </summary>
            <pre
              style={{
                marginTop: 10,
                fontSize: 9.5,
                color: T.text2,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: FONTS.code,
              }}
            >
              {error.stack}
            </pre>
          </details>
        )}

        {info?.componentStack && (
          <details
            style={{
              background: T.bg3,
              border: `1px solid ${T.border}`,
              borderRadius: 6,
              padding: "10px 14px",
              marginBottom: 14,
            }}
          >
            <summary
              style={{
                fontSize: 9,
                color: T.amber,
                cursor: "pointer",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Component stack
            </summary>
            <pre
              style={{
                marginTop: 10,
                fontSize: 9.5,
                color: T.text2,
                whiteSpace: "pre-wrap",
                fontFamily: FONTS.code,
              }}
            >
              {info.componentStack}
            </pre>
          </details>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={this.reset}
            style={{
              padding: "8px 18px",
              background: `${T.green}15`,
              color: T.green,
              border: `1px solid ${T.green}`,
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 10,
              fontFamily: FONTS.mono,
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            ↻ TRY AGAIN
          </button>
          <button
            onClick={this.copyToClipboard}
            style={{
              padding: "8px 18px",
              background: `${T.cyan}15`,
              color: T.cyan,
              border: `1px solid ${T.cyan}`,
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 10,
              fontFamily: FONTS.mono,
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            📋 COPY DETAILS
          </button>
          <button
            onClick={() => {
              if (
                window.confirm(
                  "This will clear saved state for this app and reload. Continue?",
                )
              ) {
                try {
                  localStorage.removeItem("clm_mission_control_v9");
                  localStorage.removeItem("clm_mission_control_v10");
                } catch {
                  /* ignore */
                }
                window.location.reload();
              }
            }}
            style={{
              padding: "8px 18px",
              background: `${T.red}10`,
              color: T.red,
              border: `1px solid ${T.red}`,
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 10,
              fontFamily: FONTS.mono,
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            🗑 RESET APP
          </button>
        </div>

        <div style={{ marginTop: 16, fontSize: 9, color: T.text3, fontStyle: "italic" }}>
          If you see this repeatedly, copy the details and open a ticket.
          State in other tabs has been preserved.
        </div>
      </div>
    );
  }
}
